import { CalendarPlus } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { BulkCreateModal } from '@/components/scheduling/BulkCreateModal';
import { InterviewerFilter } from '@/components/scheduling/InterviewerFilter';
import { SlotCalendar } from '@/components/scheduling/SlotCalendar';
import { SlotCreateModal } from '@/components/scheduling/SlotCreateModal';
import { SlotDetailsDrawer } from '@/components/scheduling/SlotDetailsDrawer';
import { TodayBookings } from '@/components/scheduling/TodayBookings';
import { cn } from '@/lib/utils';
import {
  bulkCreateSlots,
  createSlot,
  deleteSlot,
  listInterviewers,
  listSlots,
  markOutcome,
  updateSlot,
} from '@/services/schedulingService';

import type { BulkCreateParams, CreateSlotParams, InterviewSlot, Interviewer } from '@/types/scheduling.types';

interface CreateModalDefaults {
  date?: string;
  startTime?: string;
  endTime?: string;
}

export function SchedulingPage() {
  const { t } = useTranslation();
  const [slots, setSlots] = useState<InterviewSlot[]>([]);
  const [interviewers, setInterviewers] = useState<Interviewer[]>([]);
  const [activeInterviewerIds, setActiveInterviewerIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createModalDefaults, setCreateModalDefaults] = useState<CreateModalDefaults>({});
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<InterviewSlot | null>(null);
  const [activeTab, setActiveTab] = useState<'calendar' | 'today'>('calendar');
  const dateRangeRef = useRef<{ start: string; end: string } | null>(null);

  useEffect(() => {
    const load = async () => {
      const interviewerList = await listInterviewers();
      setInterviewers(interviewerList);
      setActiveInterviewerIds(new Set(interviewerList.map((i) => i.userId)));
    };
    void load();
  }, []);

  const fetchSlots = useCallback(async () => {
    if (!dateRangeRef.current) return;
    setIsLoading(true);
    try {
      const data = await listSlots(dateRangeRef.current.start, dateRangeRef.current.end);
      setSlots(data);
    } catch {
      toast.error(t('scheduling.toasts.error'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const handleDatesSet = useCallback(
    (start: string, end: string) => {
      const prev = dateRangeRef.current;
      if (prev?.start === start && prev.end === end) return;
      dateRangeRef.current = { start, end };
      void fetchSlots();
    },
    [fetchSlots],
  );

  const handleDateClick = useCallback((date: string, startTime: string, endTime: string) => {
    setCreateModalDefaults({ date, startTime, endTime });
    setIsCreateModalOpen(true);
  }, []);

  const handleSelect = useCallback((startStr: string, endStr: string) => {
    const start = new Date(startStr);
    const end = new Date(endStr);
    const y = start.getFullYear();
    const m = String(start.getMonth() + 1).padStart(2, '0');
    const d = String(start.getDate()).padStart(2, '0');
    const startTime = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
    const endTime = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
    setCreateModalDefaults({ date: `${y}-${m}-${d}`, startTime, endTime });
    setIsCreateModalOpen(true);
  }, []);

  const handleEventDrop = useCallback(
    async (slotId: string, newStart: string, newEnd: string) => {
      try {
        await updateSlot(slotId, { startAtUtc: newStart, endAtUtc: newEnd });
        toast.success(t('scheduling.toasts.updateSuccess'));
        await fetchSlots();
      } catch {
        toast.error(t('scheduling.toasts.error'));
        await fetchSlots();
      }
    },
    [fetchSlots, t],
  );

  const handleEventResize = useCallback(
    async (slotId: string, newStart: string, newEnd: string) => {
      try {
        await updateSlot(slotId, { startAtUtc: newStart, endAtUtc: newEnd });
        toast.success(t('scheduling.toasts.updateSuccess'));
        await fetchSlots();
      } catch {
        toast.error(t('scheduling.toasts.error'));
        await fetchSlots();
      }
    },
    [fetchSlots, t],
  );

  const handleEventClick = useCallback(
    (slotId: string) => {
      const slot = slots.find((s) => s.slotId === slotId);
      if (slot) {
        setSelectedSlot(slot);
      }
    },
    [slots],
  );

  const handleCreateSlot = useCallback(
    async (params: CreateSlotParams) => {
      await createSlot(params);
      toast.success(t('scheduling.toasts.createSuccess'));
      setIsCreateModalOpen(false);
      await fetchSlots();
    },
    [fetchSlots, t],
  );

  const handleBulkCreate = useCallback(
    async (params: BulkCreateParams) => {
      const created = await bulkCreateSlots(params);
      toast.success(t('scheduling.toasts.bulkCreateSuccess', { count: created.length }));
      setIsBulkModalOpen(false);
      await fetchSlots();
    },
    [fetchSlots, t],
  );

  const handleDeleteSlot = useCallback(
    async (slotId: string) => {
      await deleteSlot(slotId);
      toast.success(t('scheduling.toasts.deleteSuccess'));
      setSelectedSlot(null);
      await fetchSlots();
    },
    [fetchSlots, t],
  );

  const handleInterviewerToggle = useCallback((userId: string) => {
    setActiveInterviewerIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    setActiveInterviewerIds((prev) => {
      if (prev.size === interviewers.length) {
        return new Set();
      }
      return new Set(interviewers.map((i) => i.userId));
    });
  }, [interviewers]);

  const handleMarkOutcome = useCallback(
    async (bookingId: string, outcome: 'ATTENDED' | 'NOSHOW') => {
      try {
        await markOutcome(bookingId, outcome);
        toast.success(t('scheduling.attendance.markedSuccess'));
        await fetchSlots();
      } catch {
        toast.error(t('scheduling.toasts.error'));
      }
    },
    [fetchSlots, t],
  );

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900'>{t('scheduling.title')}</h1>
          <p className='mt-1 text-sm text-gray-500'>{t('scheduling.subtitle')}</p>
        </div>
        <div className='flex items-center gap-3'>
          {activeTab === 'calendar' && (
            <button
              onClick={() => setIsBulkModalOpen(true)}
              className='inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700'>
              <CalendarPlus className='h-4 w-4' />
              {t('scheduling.bulkCreate')}
            </button>
          )}
        </div>
      </div>

      <div className='flex gap-1 border-b border-gray-200'>
        <button
          onClick={() => setActiveTab('calendar')}
          className={cn(
            '-mb-px border-b-2 px-4 py-2 text-sm font-medium',
            activeTab === 'calendar'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700',
          )}>
          {t('scheduling.title')}
        </button>
        <button
          onClick={() => setActiveTab('today')}
          className={cn(
            '-mb-px border-b-2 px-4 py-2 text-sm font-medium',
            activeTab === 'today'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700',
          )}>
          {t('scheduling.attendance.tab')}
        </button>
      </div>

      {activeTab === 'calendar' && (
        <>
          <InterviewerFilter
            interviewers={interviewers}
            activeIds={activeInterviewerIds}
            onToggle={handleInterviewerToggle}
            onToggleAll={handleToggleAll}
          />

          {isLoading && slots.length === 0 ? (
            <div className='flex h-96 items-center justify-center'>
              <div className='h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent' />
            </div>
          ) : (
            <SlotCalendar
              slots={slots}
              visibleInterviewerIds={activeInterviewerIds}
              selectedSlotId={selectedSlot?.slotId ?? null}
              onDateClick={handleDateClick}
              onSelect={handleSelect}
              onEventDrop={(id, s, e) => void handleEventDrop(id, s, e)}
              onEventResize={(id, s, e) => void handleEventResize(id, s, e)}
              onEventClick={handleEventClick}
              onDatesSet={handleDatesSet}
            />
          )}

          <SlotCreateModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onSave={handleCreateSlot}
            interviewers={interviewers}
            initialDate={createModalDefaults.date}
            initialStartTime={createModalDefaults.startTime}
            initialEndTime={createModalDefaults.endTime}
          />

          <BulkCreateModal
            isOpen={isBulkModalOpen}
            onClose={() => setIsBulkModalOpen(false)}
            onSave={handleBulkCreate}
            interviewers={interviewers}
          />

          <SlotDetailsDrawer
            isOpen={!!selectedSlot}
            slot={selectedSlot}
            onClose={() => setSelectedSlot(null)}
            onDelete={handleDeleteSlot}
            onMarkOutcome={handleMarkOutcome}
          />
        </>
      )}

      {activeTab === 'today' && <TodayBookings interviewers={interviewers} />}
    </div>
  );
}
